import { ArrowLeftRight, ChevronRight, Home } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { lsGet, lsSet } from '../../model/db';
import { childrenOf, graphOf, parentsOf, partnersOf, siblingsOf } from '../../model/graph';
import { headlineRelations, RANK, relate, stepLabel, type Relation, type Step } from '../../model/kinship';
import { displayName, pronounSet, shortName } from '../../model/people';
import type { Id, Tree } from '../../model/types';
import { Avatar, JoinedTerms } from '../components/common';
import { PersonPicker } from '../components/PersonPicker';
import { commit, getState, setState, showPerson, toast, useApp } from '../store';

export function RelateView() {
  const tree = useApp((s) => s.tree)!;
  const b = useApp((s) => s.relateB);
  const home = tree.homePersonId && tree.people[tree.homePersonId] ? tree.homePersonId : null;
  // The first person is always the home person; choosing someone else there makes them home.
  const [choosingA, setChoosingA] = useState(false);
  const a = choosingA ? null : home;
  const makeHome = (id: Id) => {
    if (id === getState().tree?.homePersonId) return;
    commit({ ...getState().tree!, homePersonId: id });
    const p = getState().tree!.people[id];
    toast(`${shortName(p)} is now the home person. Relationships everywhere are described relative to ${pronounSet(p).obj}.`, 'success');
  };

  // People recently compared, for switching back and forth with one click.
  const recentKey = `relationships.relateRecent.${tree.id}`;
  const [recent, setRecent] = useState<Id[]>(() => loadRecent(recentKey));
  useEffect(() => setRecent(loadRecent(recentKey)), [recentKey]);
  useEffect(() => {
    if (!b) return;
    setRecent((r) => {
      // New people go first; picking someone already listed leaves the order alone so chips don't jump.
      if (r.includes(b)) return r;
      const next = [b, ...r].slice(0, RECENT_MAX);
      lsSet(recentKey, JSON.stringify(next));
      return next;
    });
  }, [b, recentKey]);
  const family = useMemo(() => {
    if (!a) return [];
    const g = graphOf(tree);
    return [...new Set([...partnersOf(g, a), ...parentsOf(g, a), ...siblingsOf(g, a).map((s) => s.id), ...childrenOf(g, a)])];
  }, [tree, a]);
  // Recent leaves out anyone already a click away in the family row.
  const familyShown = family.slice(0, 12);
  const recentShown = recent.filter((id) => id !== a && tree.people[id] && !familyShown.includes(id));

  const result = useMemo(() => (a && b ? relate(tree, a, b) : null), [tree, a, b]);
  const reverse = useMemo(() => (a && b ? relate(tree, b, a) : null), [tree, a, b]);
  // Someone related more than one direct way (best friend and chosen sister) is described as all of them.
  const lead = useMemo(() => (result ? headlineRelations(result) : []), [result]);
  const revLead = useMemo(() => (reverse ? headlineRelations(reverse) : []), [reverse]);
  const primary = lead[0];

  return (
    <div className="page">
      <div className="page-inner">
        <div>
          <h1 style={{ fontSize: 22 }}>How are they related?</h1>
          <p className="muted" style={{ marginTop: 4 }}>
            Pick any two people. The app checks blood and legal kinship, partners and in-laws, step-family, chosen family, friends, and shared groups, and shows the exact path between them.
          </p>
        </div>
        <div className="card-box">
          <div className="relate-pickers">
            <div className="field">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <label className="field-label">
                  First person <span className="badge accent" style={{ marginLeft: 4 }}><Home size={11} /> Home person</span>
                </label>
                {choosingA && home && (
                  <button className="add-link" style={{ padding: 0 }} onClick={() => setChoosingA(false)}>
                    Keep {shortName(tree.people[home])}
                  </button>
                )}
              </div>
              <PersonPicker
                tree={tree}
                value={a}
                onChange={(id) => {
                  if (!id) return setChoosingA(true);
                  makeHome(id);
                  setChoosingA(false);
                }}
                autoFocus={choosingA}
                ariaLabel="First person (home person)"
                placeholder="Choose the home person…"
              />
            </div>
            <button
              className="btn icon"
              aria-label="Swap people"
              title="Swap (the second person becomes the home person)"
              disabled={!a || !b}
              onClick={() => {
                if (!a || !b) return;
                makeHome(b);
                setState({ relateB: a });
              }}
              style={{ marginBottom: 1 }}
            >
              <ArrowLeftRight size={16} />
            </button>
            <div className="field">
              <label className="field-label">Second person</label>
              <PersonPicker tree={tree} value={b} onChange={(relateB) => setState({ relateB })} ariaLabel="Second person" />
            </div>
          </div>
          {/* Family first: it only changes with the home person, so the Recent row appearing doesn't move it. */}
          {a && family.length > 0 && <QuickPicks tree={tree} label={`${shortName(tree.people[a])}’s family`} ids={familyShown} current={b} />}
          {a && recentShown.length > 0 && <QuickPicks tree={tree} label="Recent" ids={recentShown} current={b} />}
          <p className="faint small-text" style={{ marginTop: 8 }}>
            Click either name to swap in someone else. The first person is the home person, whom relationships are described relative to throughout the app, so choosing someone else there makes them the home person.
          </p>
        </div>

        {a && b && result && (
          <>
            <div className="card-box" aria-live="polite">
              {primary ? (
                <>
                  <div className="answer">
                    {primary.category === 'self' ? (
                      'That’s the same person.'
                    ) : (
                      <>
                        {displayName(tree.people[b])} is {displayName(tree.people[a])}’s <JoinedTerms terms={lead.map((r) => r.term)} className="term" />.
                      </>
                    )}
                  </div>
                  {revLead.length > 0 && primary.category !== 'self' && (
                    <div className="answer-sub">
                      And {shortName(tree.people[a])} is {shortName(tree.people[b])}’s <JoinedTerms terms={revLead.map((r) => r.term)} />.
                    </div>
                  )}
                  {lead.map((r) => (
                    <div key={r.term} className="lead-detail">
                      {lead.length > 1 && <div className="field-label">As {r.term}</div>}
                      <p className="muted">{r.explanation}</p>
                      <PathView tree={tree} path={r.path} start={a} />
                      {r.commonAncestors.length > 0 && (
                        <div style={{ marginTop: 14 }}>
                          <div className="field-label" style={{ marginBottom: 6 }}>
                            Common ancestor{r.commonAncestors.length > 1 ? 's' : ''}
                          </div>
                          <div className="row wrap">
                            {r.commonAncestors.map((id) => (
                              <PersonChip key={id} tree={tree} id={id} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="answer">No connection found.</div>
                  <p className="muted" style={{ marginTop: 6 }}>
                    {displayName(tree.people[a])} and {displayName(tree.people[b])} aren’t linked by any recorded relationship, friendship, or group. Add the missing link and try again.
                  </p>
                </>
              )}
            </div>
            {result.relations.length > lead.length && (
              <div className="card-box">
                <h3 style={{ fontSize: 15, marginBottom: 10 }}>Also related as</h3>
                <div className="also-list">
                  {result.relations.filter((r) => !lead.includes(r)).map((r, i) => (
                    <AlsoItem key={i} tree={tree} rel={r} a={a} b={b} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {(!a || !b) && (
          <div className="card-box muted">
            <b>Tips</b>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              <li>Open anyone’s details and choose “How related?” to compare them with the home person.</li>
              <li>Each person is described with the relationship words chosen in their editor (feminine, masculine, or neutral). Automatic follows their pronouns, then their gender, and is neutral otherwise.</li>
              <li>Half-, double, step-, adoptive, and chosen relationships are named when the tree has enough detail.</li>
            </ul>
          </div>
        )}
        <RankingHelp />
      </div>
    </div>
  );
}

/** Example relations at each score, closest first. Blood relations score their generations (see RANK). */
const RANK_ROWS: { score: number; what: string }[] = [
  { score: RANK.partner, what: 'Partner (current or widowed)' },
  { score: 1, what: 'Parent or child' },
  { score: RANK.bestFriend, what: 'Best friend' },
  { score: 2, what: 'Sibling, grandparent, or grandchild' },
  { score: 1 + RANK.inLaw, what: 'Parent-in-law, child-in-law, stepparent, or stepchild' },
  { score: RANK.connection, what: 'Friend, close friend, mentor, roommate, colleague, or neighbor' },
  { score: RANK.stepsibling, what: 'Stepsibling' },
  { score: RANK.foundFamily, what: 'In the same found family' },
  { score: 3, what: 'Aunt, uncle, niece, nephew, great-grandparent, or great-grandchild' },
  { score: 2 + RANK.inLaw, what: 'Sibling-in-law' },
  { score: RANK.familyFriend, what: 'Family friend' },
  { score: 4, what: 'First cousin' },
  { score: RANK.sharedGroup, what: 'In the same group of another kind' },
  { score: RANK.formerPartner, what: 'Former partner' },
  { score: RANK.chain, what: 'A chain of links, like “mother’s best friend’s son”, only when nothing above applies' },
].sort((x, y) => x.score - y.score);

/** How the answer is chosen when two people are related in more than one way. Collapsed until asked for. */
function RankingHelp() {
  return (
    <details className="card-box relate-ranking">
      <summary>How this works</summary>
      <div className="stack small-text" style={{ marginTop: 10 }}>
        <p>
          Two people can be related in several ways at once. Each way gets a score for how close it is, and the answer leads with the lowest. When that’s a direct tie (a parent, child, sibling,
          partner, or friend), every other direct tie is named with it, as in “best friend and chosen sister.” Everything else is under “Also related as,” closest first.
        </p>
        <table className="rank-table">
          <thead>
            <tr>
              <th scope="col">Score</th>
              <th scope="col">Relation</th>
            </tr>
          </thead>
          <tbody>
            {RANK_ROWS.map((r) => (
              <tr key={r.what}>
                <td>{r.score === RANK.chain ? `${r.score}+` : r.score}</td>
                <td>{r.what}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          <b>Blood and legal relatives</b> score the generations between them: up from the first person to the closest common ancestor, then down to the second. A second cousin is 6, a first
          cousin once removed 5. Half-, adoptive, step-, and chosen relatives score the same as full ones; when several paths give the same relationship, the one with the fewest
          non-biological links names it.
        </p>
        <p>
          <b>In-laws and step-relations</b> score the relative they go through plus {RANK.inLaw}, or plus {Math.round((RANK.inLaw + RANK.unmarriedInLaw) * 10) / 10} when the partner in between
          isn’t married or in a civil union or domestic partnership. So if your spouse’s mother is also your aunt, she’s your mother-in-law first ({1 + RANK.inLaw}) and your aunt second (3).
        </p>
        <p>The same relationship found along two paths is listed once, at its closest.</p>
      </div>
    </details>
  );
}

const RECENT_MAX = 8;

function loadRecent(key: string): Id[] {
  try {
    const saved = JSON.parse(lsGet(key) ?? '[]');
    return Array.isArray(saved) ? saved.filter((x): x is Id => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** One-click choices for the second person. */
function QuickPicks({ tree, label, ids, current }: { tree: Tree; label: string; ids: Id[]; current: Id | null }) {
  return (
    <div className="quick-picks" role="group" aria-label={label}>
      <span className="field-label">{label}</span>
      {ids.map((id) => (
        <button key={id} className="path-person" aria-pressed={id === current} onClick={() => setState({ relateB: id })} title={`Compare with ${displayName(tree.people[id])}`}>
          <Avatar person={tree.people[id]} size="sm" />
          {shortName(tree.people[id])}
        </button>
      ))}
    </div>
  );
}

function AlsoItem({ tree, rel, a, b }: { tree: Tree; rel: Relation; a: Id; b: Id }) {
  return (
    <details>
      <summary style={{ cursor: 'pointer' }}>
        {shortName(tree.people[b])} is also {shortName(tree.people[a])}’s <b>{rel.term}</b>
      </summary>
      <p className="muted small-text" style={{ margin: '6px 0 0 16px' }}>
        {rel.explanation}
      </p>
      <div style={{ marginLeft: 16 }}>
        <PathView tree={tree} path={rel.path} start={a} />
      </div>
    </details>
  );
}

function PersonChip({ tree, id }: { tree: Tree; id: Id }) {
  const p = tree.people[id];
  if (!p) return null;
  return (
    <button className="path-person" data-person-id={id} onClick={() => showPerson(id)} title="Show in the tree">
      <Avatar person={p} size="sm" />
      {displayName(p)}
    </button>
  );
}

function PathView({ tree, path, start }: { tree: Tree; path: Step[]; start: Id }) {
  if (!path.length) return null;
  return (
    <div className="path" aria-label="Relationship path">
      <PersonChip tree={tree} id={start} />
      {path.map((s, i) => (
        <span key={i} style={{ display: 'contents' }}>
          <span className="path-step">
            <span>{stepLabel(tree, s)}</span>
            <ChevronRight size={14} className="arrow" />
          </span>
          <PersonChip tree={tree} id={s.to} />
        </span>
      ))}
    </div>
  );
}
